import { MouseEvent, useContext } from 'react';
import { Info } from 'lucide-react';
import { DateTime } from 'luxon';

import styles from './about.module.css';

import config from '@/config';

import { Modal } from '@shared/ui/modal';
import { useDialog } from '@shared/lib/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from '@/app.context';
import { exportDebugZip } from '@shared/lib/debug-log';
import { Logger } from '@shared/lib/logger';
import { sanitizeHtml } from '@/lib/article-renderer/core/sanitize-html';
import { AUTH_STORAGE_KEYS } from '@shared/auth';
import { clearPersistStorage } from '@shared/query-client';
import { observabilityDb } from '@shared/observability';

const logger = new Logger('about');

export const About = ({ show, setShow }: AboutProps) => {
	const queryClient = useQueryClient();
	const { kv, db } = useContext(AppContext);
	const { title, version, about, buildNumber } = config.build;
	// buildNumber is a Unix epoch (seconds) timestamp -- see
	// scripts/prepare.js's getBuildId().
	const buildDate = DateTime.fromSeconds(buildNumber).toLocaleString(
		DateTime.DATETIME_FULL,
	);
	const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
		switch (e.detail) {
			case 2:
				window.location.replace('/drafts');
				break;
		}
	};

	// "Clear everything except auth" -- a reader hitting this expects a true
	// reset of whatever's gone stale (a bad cached response, a stuck offline
	// download, a corrupt persisted entry), not to be logged out as a side
	// effect. Previously this only reset `kv`'s own single localStorage
	// entry, the in-memory TanStack Query cache, and the Cache Storage API
	// (`caches`) -- the persister's actual IndexedDB entries
	// (`clearPersistStorage`, keyed separately from the in-memory cache)
	// survived and were simply restored on the very next boot, as did the
	// Dexie offline-download DB (favorited issues/articles/styles), the
	// observability DB, and every other localStorage key. `AUTH_STORAGE_KEYS`
	// is the one thing deliberately spared throughout.
	const onClickClearCache = async () => {
		if (!confirm('Are you sure you want to clear the cache?')) {
			return;
		}

		kv.clear();

		const authKeys = new Set<string>(AUTH_STORAGE_KEYS);
		for (const key of Object.keys(localStorage)) {
			if (!authKeys.has(key)) {
				localStorage.removeItem(key);
			}
		}

		if (queryClient) {
			queryClient.clear();
		}
		await clearPersistStorage().catch((error: unknown) => {
			logger.error('Failed to clear the persisted query cache', error);
		});

		if (db) {
			await db.clear().catch((error: unknown) => {
				logger.error('Failed to clear the offline downloads DB', error);
			});
		}

		await Promise.all([
			observabilityDb.events.clear(),
			observabilityDb.documents.clear(),
			observabilityDb.meta.clear(),
		]).catch((error: unknown) => {
			logger.error('Failed to clear the observability DB', error);
		});

		if ('caches' in window) {
			const names = await caches.keys();
			// Spares the Workbox-managed precache (`workbox-precache-v2-...`,
			// `src/sw.ts`'s `precacheAndRoute(self.__WB_MANIFEST)`) -- the app
			// shell (index.html/JS/CSS) the still-active SW instance's
			// `NavigationRoute` falls back to when a reload's own network
			// fetch is slow or fails. Deleting it here, immediately before
			// forcing that same reload, knocks out its own safety net right
			// when it's most likely to be needed: every runtime cache below
			// it is being wiped in the same breath, so the reload's asset
			// requests all miss cache and hit the network at once, and if
			// the navigation's `NetworkFirst` swallows its 4s timeout under
			// that load, the fallback it lands on would otherwise be an
			// empty cache instead of the real app shell -- reported as the
			// app "keeps refreshing" after clicking Clear Cache. Everything
			// reader-facing (downloaded media, the navigation/version
			// caches) is still cleared; only the shell Workbox needs to keep
			// functioning survives.
			await Promise.all(
				names
					.filter((name) => !name.startsWith('workbox-precache'))
					.map((name) => caches.delete(name)),
			);
		}

		window.location.reload();
	};

	// Ungated by design (improvements/17-debug-capture-export.md): the
	// export contains nothing sensitive by construction (no user
	// identifiers, no tokens, no response bodies -- see the observability
	// pipeline's own constraints), so any reader can self-serve it and
	// send the file over, no support call required.
	const onClickExportDebugInfo = () => {
		exportDebugZip().catch((error: unknown) => {
			logger.error('Failed to export debug info', error);
		});
	};

	// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
	const v = <span onClick={handleClick}> {version}</span>;

	return (
		<Modal
			classNames={[styles['container']]}
			hasCloseBtn={true}
			isOpen={show}
			onClose={() => {
				setShow(false);
			}}
		>
			<div className={styles['about-dialog']}>
				<h3>{title}</h3>
				<h4>Version: {v}</h4>
				<h4 title={buildDate}>Build: {buildNumber}</h4>
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
				<h4
					onClick={onClickClearCache}
					className={styles['clear-cache']}
				>
					Clear Cache
				</h4>
				{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
				<h4
					onClick={onClickExportDebugInfo}
					className={styles['clear-cache']}
				>
					Export Debug Info
				</h4>
				{about ? (
					<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(about) }} />
				) : null}
			</div>
		</Modal>
	);
};

interface AboutProps {
	show: boolean;
	setShow: (value: boolean) => void;
}

export function AboutButton() {
	const { setShowDialog } = useDialog();
	const className = [styles['about'], 'tooltip down cursor-pointer'].join(
		' ',
	);

	const onClick = () => {
		setShowDialog(true);
	};

	return (
		<button
			type="button"
			className={className}
			data-text="About"
			onClick={onClick}
		>
			<Info color="var(--topbar-buttons-stroke)" />
		</button>
	);
}
